import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Seo } from "../components/cod/Seo";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { staffJobsApi } from "../lib/api";
import { toast } from "sonner";
import {
  Briefcase, Users, MapPin, DollarSign, Clock, Search, CheckCircle2, Building2,
} from "lucide-react";

const STATUS_LABELS = { draft: "Draft", open: "Open", on_hold: "On Hold", filled: "Filled", closed: "Closed" };
const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  open: "bg-emerald-100 text-emerald-800",
  on_hold: "bg-amber-100 text-amber-800",
  filled: "bg-blue-100 text-blue-800",
  closed: "bg-slate-200 text-slate-600",
};
const PRIORITY_COLORS = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-slate-100 text-slate-700",
  high: "bg-amber-100 text-amber-800",
  critical: "bg-red-100 text-red-800",
};

export function StaffJobOrderQueue() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [q, setQ] = useState("");
  const [markFill, setMarkFill] = useState(null);
  const [notes, setNotes] = useState("");
  const load = () => staffJobsApi.list(status).then(setRows).catch(()=>setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return rows;
    return rows.filter(r => (r.role_title || "").toLowerCase().includes(s)
                          || (r.organization_name || "").toLowerCase().includes(s)
                          || (r.location || "").toLowerCase().includes(s));
  }, [rows, q]);

  const doMarkFilled = async () => {
    if (!markFill) return;
    try {
      await staffJobsApi.markFilled(markFill.id, notes);
      toast.success(`Marked '${markFill.role_title}' as filled — removed from queue`);
      setMarkFill(null); setNotes("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openCount = rows.filter(r => r.status === "open").length;
  const totalCandidates = rows.reduce((s, r) => s + (r.candidate_counts?.referred || 0), 0);

  return (
    <AppShell>
      <Seo title="Staff · Job Orders Queue" path="/staff/job-orders" noindex/>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">Business Development</div>
          <h1 className="font-serif text-3xl text-slate-900 mt-1">Employer Job Orders Queue</h1>
          <div className="cod-rule-red mt-3"/>
          <p className="text-sm text-slate-600 mt-3 max-w-3xl">Every job order posted by employers across the network. Match candidates from active cohorts, then click <strong>Employee Found</strong> to close the order and remove it from the queue.</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Open orders" value={openCount} color="emerald"/>
          <Stat label="Candidates in flight" value={totalCandidates} color="blue"/>
          <Stat label="Total in view" value={rows.length} color="slate"/>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input placeholder="Search role, employer, location…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="staff-jobs-search"/>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="staff-jobs-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="staff-jobs-list">
        {filtered.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">
            No job orders match those filters.
          </div>
        )}
        {filtered.map(j => (
          <Card key={j.id} className="border-slate-200" data-testid={`staff-job-${j.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-slate-900">{j.role_title}</div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_COLORS[j.status]}`}>{STATUS_LABELS[j.status]}</span>
                    {j.priority && j.priority !== "medium" && (
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${PRIORITY_COLORS[j.priority]}`}>{j.priority}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                    <Building2 size={12}/>
                    <Link to={`/staff/bd/organizations/${j.organization_id}`} className="cod-link">{j.organization_name || j.organization_id}</Link>
                  </div>
                  <div className="text-xs text-slate-600 mt-2 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Users size={12}/> {j.headcount} FTE ({j.filled_count || 0} filled)</span>
                    {j.wage_min && <span className="flex items-center gap-1"><DollarSign size={12}/> ${j.wage_min}{j.wage_max ? `–$${j.wage_max}` : ""}/{j.wage_unit}</span>}
                    {j.location && <span className="flex items-center gap-1"><MapPin size={12}/> {j.location}</span>}
                    {j.start_date && <span className="flex items-center gap-1"><Clock size={12}/> starts {String(j.start_date).slice(0,10)}</span>}
                  </div>
                  {j.description && <p className="text-sm text-slate-700 mt-2 line-clamp-2">{j.description}</p>}
                  {(j.certifications_required || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {j.certifications_required.map((c, i) => <span key={i} className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{c}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {j.status === "open" || j.status === "on_hold" ? (
                    <Button className="cod-bg-red" size="sm" onClick={()=>setMarkFill(j)} data-testid={`btn-mark-filled-${j.id}`}>
                      <CheckCircle2 size={14} className="mr-1"/>Employee Found
                    </Button>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">Closed</span>
                  )}
                  <Link to={`/staff/bd/organizations/${j.organization_id}`}>
                    <Button size="sm" variant="outline" data-testid={`btn-org-${j.id}`}>View employer</Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!markFill} onOpenChange={(v)=>{ if(!v) { setMarkFill(null); setNotes(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark job order as filled</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-700">This will close <strong>{markFill?.role_title}</strong> ({markFill?.organization_name}) and remove it from the queue. The employer will be notified.</div>
          <div>
            <label className="text-sm text-slate-700">Notes (optional)</label>
            <textarea rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm mt-1"
              placeholder="e.g., Placed 3 candidates from CNA cohort" value={notes} onChange={e=>setNotes(e.target.value)}
              data-testid="mark-filled-notes"/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={()=>{ setMarkFill(null); setNotes(""); }}>Cancel</Button>
            <Button className="cod-bg-red" onClick={doMarkFilled} data-testid="mark-filled-confirm">Confirm & Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Stat({ label, value, color }) {
  const colors = {
    emerald: "text-emerald-700 border-emerald-200",
    blue: "text-blue-700 border-blue-200",
    slate: "text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-lg border ${colors[color]} bg-white px-3 py-2 min-w-[100px]`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className="text-2xl font-serif">{value}</div>
    </div>
  );
}
